import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { cloneDeep } from 'lodash-es';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateModule } from '@ngx-translate/core';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { AssistantTasksService } from '@nimbus-assistant/services/assistant-tasks.service';
import { TaskToastService } from '@nimbus-assistant/services/task-toast.service';
import { TaskState, ITask } from '@nimbus-assistant/models/task.model';
import { Modality } from '@nimbus-assistant/models/task-types.model';
import { HomeImages } from '@nimbus-assistant/enums/home-images';
import { TASK_TABS, TASK_TAB_CODES, TASK_TAB_FLOW, ITaskTab } from '@nimbus-assistant/models/task-details-tabs';
import { ICONS } from '@nimbus-library/features/shared/resources/icons';
import { IconWeight } from '@nimbus-library/features/shared/directives/icon-weight.enum';
import { TranslationKeys } from '@nimbus-library/features/localization/models/enums/translation-keys.enum';
import { SharedUIModule } from '@nimbus-library/features/shared/shared.ui.module';
import { TaskCodesTabComponent } from './task-codes-tab/task-codes-tab.component';
import { TaskFlowTabComponent } from './task-flow-tab/task-flow-tab.component';
import { TaskContextTabComponent } from './task-context-tab/task-context-tab.component';

@UntilDestroy()
@Component({
  standalone: true,
  selector: 'app-task-details',
  templateUrl: './task-details.component.html',
  styleUrls: ['./task-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, TranslateModule, SharedUIModule, TaskCodesTabComponent, TaskFlowTabComponent, TaskContextTabComponent],
})
export class TaskDetailsComponent implements OnInit {
  private static readonly DISPLAY_STATES = [TaskState.Connected, TaskState.InACW, TaskState.InExtendedACW];
  public readonly ICONS = ICONS;

  public readonly IconWeight = IconWeight;
  public readonly TranslationKeys = TranslationKeys;
  public readonly onDutyImage = HomeImages.onDutyImage;
  public readonly isExtendingTeamsToast$ = this.taskToastService.isExtendingTeamsToast$;
  public displayTask: ITask | null = null;
  public isDetailsLoaded = false;
  public tabItems: ITaskTab[] = [];

  public constructor(
    private readonly tasksService: AssistantTasksService,
    private readonly taskToastService: TaskToastService,
    private readonly cd: ChangeDetectorRef,
  ) {}

  public ngOnInit(): void {
    this.tasksService.tasks$
      .pipe(
        map((tasks) => tasks.find((t) => TaskDetailsComponent.DISPLAY_STATES.includes(t.state)) ?? null),
        distinctUntilChanged(),
        untilDestroyed(this),
      )
      .subscribe((task) => {
        const idChanged = this.displayTask?.unifiedConversationId !== task?.unifiedConversationId;
        const wasDetailsLoaded = this.isDetailsLoaded;
        const isDetailsLoaded = task ? this.tasksService.isDetailsLoaded(task.unifiedConversationId) : false;
        const detailsJustLoaded = !wasDetailsLoaded && isDetailsLoaded;
        const stateChanged = this.displayTask?.state !== task?.state;
        this.displayTask = task;
        this.isDetailsLoaded = isDetailsLoaded;
        if (idChanged || detailsJustLoaded || stateChanged) {
          this.resetTabs(task);
        }
        this.cd.markForCheck();
      });

    this.taskToastService.extendedTabItems$.pipe(untilDestroyed(this)).subscribe((extendedTabs) => {
      if (extendedTabs) {
        this.tabItems = extendedTabs;
      } else if (this.displayTask) {
        this.resetTabs(this.displayTask);
      }
      this.cd.markForCheck();
    });
  }

  public navigateTo(id: string): void {
    this.tabItems.forEach((tab) => (tab.isActive = tab.id === id));
  }

  public isTabActive(id: string): boolean {
    return this.tabItems.find((tab) => tab.id === id)?.isActive;
  }

  private resetTabs(task: ITask | null): void {
    if (!task || task.isDirectCall || (task.isServiceCall && !this.isDetailsLoaded)) {
      this.tabItems = [];
      return;
    }

    const codesAndTagsDisabled = !task.conversationCodes?.codesAndTagsEnabled;
    this.tabItems = cloneDeep(codesAndTagsDisabled ? TASK_TABS.filter((x) => x.id !== TASK_TAB_CODES) : TASK_TABS);

    if ([Modality.IM, Modality.ExternalTask, Modality.Email].includes(task.modality)) {
      const flowTab = this.tabItems.find((x) => x.id === TASK_TAB_FLOW);
      if (flowTab) {
        flowTab.name = TranslationKeys.CallTabsChatFlow;
      }
    }
  }
}
